<template>
  <v-dialog
    :value="dialog"
    :max-width="320"
    persistent>
    <v-card>

      <!-- extend fudao dialog -->
      <template v-if="extendFudaoSent">
        <v-card-text class="subheading">已送出延長輔導邀請，等待對方回應...</v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <slot name="action"></slot>
          <v-btn
            v-show="showCancelInvtieBtn"
            @click="cancelInvite"
            flat>
            取消邀請
          </v-btn>
        </v-card-actions>
      </template>

      <!-- timeup dialog -->
      <v-card-text v-else class="overflow-y-hidden">
        <div class="title text-xs-center">輔導時間已到</div>
        <div class="d-flex pt-4">
          <template v-if="loading">
            <v-progress-circular
              :size="50"
              color="accent"
              indeterminate />
          </template>
          <template v-else>
            <div class="action-btn" @click="extendFudao">
              <v-icon class="action-btn__icon">restore</v-icon>
              <span>延長輔導</span>
            </div>
            <div class="action-btn" @click="endFudao">
              <v-icon class="action-btn__icon">cancel_presentation</v-icon>
              <span>結束輔導</span>
            </div>
          </template>
        </div>
        <!-- 倒數progress -->
        <v-progress-linear
          v-if="countdownProgress"
          v-model="countdownPercentage"
          color="secondary" />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {mapGetters, mapState, mapActions} from 'vuex';
import {FudaoEvent, fudaoAppService} from 'langyan';

const getDefaultData = () => {
  return {
    loading: false,
    dialog: false,
    extendFudaoSent: false,
    waitingTime: 0,
    showCancelInvtieBtn: false,
    countdownProgress: true
  };
};

export default {
  data: getDefaultData,
  computed: {
    ...mapState('fudao', {
      fudaoId: state => state.fudaoDto.id,
      responseTimeout: state => state.responseTimeout
    }),

    ...mapGetters('fudao', {
      isFudaoTeacher: 'isFudaoTeacher'
    }),

    countdownPercentage() {
      return Math.round(this.waitingTime/(this.responseTimeout/1000) * 100);
    }
  },

  watch: {
    dialog(value) {
      if (value) {
        this._countdownTimeInterval = setInterval(() => {
          this.waitingTime++;
        }, 1000);
      } else {
        this.reset();
      }
    },

    waitingTime(value) {
      if (value < this.responseTimeout/1000) {
        return;
      }
      // is timeout
      this.endFudao();
    }
  },

  mounted() {
    this.$eventBus.$on(FudaoEvent.FudaoTimeUp, this.onFudaoTimeUp);
    this.$eventBus.$on(FudaoEvent.FudaoExtended, this.onFudaoExtended);
    this.$eventBus.$on(FudaoEvent.FudaoEnded, this.onFudaoEnded);
  },

  beforeDestory() {
    this.$eventBus.$off(FudaoEvent.FudaoTimeUp, this.onFudaoTimeUp);
    this.$eventBus.$off(FudaoEvent.FudaoExtended, this.onFudaoExtended);
    this.$eventBus.$off(FudaoEvent.FudaoEnded, this.onFudaoEnded);
  },

  methods: {
    ...mapActions('alert', {
      addAlert: 'add'
    }),

    isDifferentFudao(domainEvent) {
      return this.fudaoId !== domainEvent.body.id;
    },

    stopCountdownProgress() {
      clearInterval(this._countdownTimeInterval);
      this.countdownProgress = false;
    },

    open() {
      this.dialog = true;
    },

    async extendFudao() {
      this.stopCountdownProgress();
      this.loading;
      await fudaoAppService.inviteExtendFudaoAsync(this.fudaoId);

      // end fudao if invitation has no response.
      this._inviteExtendTimeout = setTimeout(() => {
        this.sentNoResponse();
      }, this.responseTimeout);
    },

    endFudao() {
      this.stopCountdownProgress();
      this.loading = true;
      fudaoAppService.endFudaoAsync(this.fudaoId);
    },

    reset() {
      this.$eventBus.$off(FudaoEvent.TeacherExtendInvitationSent, this.onTeacherExtendInvitationSent);
      this.$eventBus.$off(FudaoEvent.StudentExtendInvitationAccepted, this.onStudentExtendInvitationAccepted);
      this.$eventBus.$off(FudaoEvent.StudentExtendInvitationRejected, this.onStudentExtendInvitationRejected);
      this.$eventBus.$off(FudaoEvent.StudentExtendInvitationNoReponse, this.onStudentExtendInvitationNoReponse);
      this.$eventBus.$off(FudaoEvent.FudaoStartFailed, this.onFudaoStartFailed);

      clearTimeout(this._inviteExtendTimeout);
      this.stopCountdownProgress();

      let data = getDefaultData();
      Object.keys(data).forEach(key => this[key] = data[key]);
    },

    // end fudao if teacher manually cancel invitation.
    async cancelInvite() {
      clearTimeout(this._inviteExtendTimeout);
      this.endFudao();
    },

    sentNoResponse() {
      fudaoAppService.noResponseExtendFuadoAsync(this.fudaoId);
    },

    // events -------------------------------------

    onTeacherExtendInvitationSent(domainEvent) {
      if (this.isDifferentFudao(domainEvent)) {
        return;
      }
      this.extendFudaoSent = true;
      this.showCancelInvtieBtn = true;
    },

    async onStudentExtendInvitationAccepted(domainEvent) {
      if (this.isDifferentFudao(domainEvent)) {
        return;
      }

      clearTimeout(this._inviteExtendTimeout);
      await fudaoAppService.extendFudaoAsync(this.fudaoId);
    },

    onStudentExtendInvitationRejected(domainEvent) {
      if (this.isDifferentFudao(domainEvent)) {
        return;
      }
      this.addAlert({level: 'info', content: '學生拒絕延長輔導'});
      this.endFudao();
    },

    onStudentExtendInvitationNoReponse(domainEvent) {
      if (this.isDifferentFudao(domainEvent)) {
        return;
      }
      this.addAlert({level: 'warning', content: '邀請延長輔導無回應'});
      this.endFudao();
    },

    onFudaoTimeUp(domainEvent) {
      // if current user is not the role of teacher in current fudao session.
      if (this.isFudaoTeacher !== true) {
        return;
      }
      if (domainEvent.body.id !== this.fudaoId) {
        return;
      }

      this.$eventBus.$on(FudaoEvent.TeacherExtendInvitationSent, this.onTeacherExtendInvitationSent);
      this.$eventBus.$on(FudaoEvent.StudentExtendInvitationAccepted, this.onStudentExtendInvitationAccepted);
      this.$eventBus.$on(FudaoEvent.StudentExtendInvitationRejected, this.onStudentExtendInvitationRejected);
      this.$eventBus.$on(FudaoEvent.StudentExtendInvitationNoReponse, this.onStudentExtendInvitationNoReponse);
      this.$eventBus.$on(FudaoEvent.FudaoStartFailed, this.onFudaoStartFailed);

      let extendAvaliable = fudaoAppService.canExtendFudao(this.fudaoId);
      // has quota to extend fudao
      if (extendAvaliable) {
        this.dialog = true;
      } else {
        fudaoAppService.endFudaoAsync(this.fudaoId);
      }
    },

    onFudaoExtended(domainEvent) {
      this.dialog = false;
    },

    onFudaoEnded(domainEvent) {
      this.dialog = false;
    },

    onFudaoStartFailed(errMsg) {
      this.addAlert({level: 'error', content: '延長or結束輔導發生錯誤: ' + errMsg});
      // force end fudao
      fudaoAppService.interruptFudaoAsync(this.fudaoId);
    }
  }
};
</script>

<style lang="stylus" scoped>

.action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    background-color: $secondary-color;
  }
}

.action-btn__icon {
  font-size: 5rem;
}
</style>
